// The account contract from the caller's side. `accountTransport` speaks it
// over HTTP for the browser; the simulation tests hand `AccountController`
// a transport that calls the handlers directly. Both get the same typed
// calls, generated from ./contract.ts.

import { Effect } from "effect";
import { FetchHttpClient, type HttpClient } from "effect/http";
import { HttpApiClient } from "effect/http-api";
import type { BuyerPlace } from "../core/seller.ts";
import { AccountApi, type AccountState, type Plan } from "./contract.ts";

/** What the browser knows of where its buyer is: the time zone, and whether the buyer said mainland China. */
export type BrowserPlace = Pick<BuyerPlace, "timeZone" | "mainland">;

/** The three calls of the contract, wherever they go. */
export interface AccountTransport {
  state(): Effect.Effect<AccountState, unknown>;
  checkout(plan: Plan, place?: BrowserPlace): Effect.Effect<{ url: string }, unknown>;
  portal(): Effect.Effect<{ url: string }, unknown>;
}

/** The contract over HTTP at `origin`. `fetcher` stands in for the network in tests. */
export function accountTransport(origin: string, fetcher: typeof globalThis.fetch = globalThis.fetch): AccountTransport {
  const client = HttpApiClient.make(AccountApi, { baseUrl: origin });
  const run = <A>(effect: Effect.Effect<A, unknown, HttpClient.HttpClient>) =>
    effect.pipe(Effect.provide(FetchHttpClient.layer), Effect.provideService(FetchHttpClient.Fetch, fetcher));
  return {
    state: () => run(Effect.flatMap(client, (api) => api.account.state({ headers: {} }))),
    checkout: (plan, place) =>
      run(
        Effect.flatMap(client, (api) =>
          api.account.checkout({
            headers: {},
            payload: { plan, ...(place?.timeZone ? { timeZone: place.timeZone } : {}), ...(place?.mainland ? { mainland: true } : {}) },
          }),
        ),
      ),
    portal: () => run(Effect.flatMap(client, (api) => api.account.portal({ headers: {} }))),
  };
}

/**
 * The account as a page holds it: the last state it read, and the calls that
 * change it. The browser and the simulation tests run this same class, so a
 * state transition tested in process is the one the page makes.
 */
export class AccountController {
  state: AccountState | null = null;

  constructor(private readonly transport: AccountTransport) {}

  /** Read the state again and keep it. */
  refresh(): Effect.Effect<AccountState, unknown> {
    return this.transport.state().pipe(
      Effect.tap((state) =>
        Effect.sync(() => {
          this.state = state;
        }),
      ),
    );
  }

  checkout(plan: Plan, place?: BrowserPlace): Effect.Effect<{ url: string }, unknown> {
    return this.transport.checkout(plan, place);
  }

  portal(): Effect.Effect<{ url: string }, unknown> {
    return this.transport.portal();
  }
}
